import EmailLink from "@/components/atoms/EmailLink";
import Flex from "@/components/atoms/Flex";
import PhoneCallLink from "@/components/atoms/PhoneCallLink";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import {
  getColorFromRole,
  getReelRoleName,
  isStrictAdmin,
} from "@/helpers/UserHelpers";
import CompanyCourseCommission from "@/interfaces/CompanyCourseCommission";
import CompanyCourseUser from "@/interfaces/CompanyCourseUser";
import {
  useGetCompanyCourseCommissionsQuery,
  useUpdateCompanyCourseCommissionMutation,
} from "@/services/companies/courses/commissions";
import { selectCurrentUser } from "@/services/slices/authSlice";
import {
  faAt,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faPercent,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import classNames from "classnames";
import { DateTime } from "luxon";
import { FC, useState } from "react";
import {
  Button,
  Col,
  Form,
  InputGroup,
  Placeholder,
  Row,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

interface ICompanyCourseUsersCardProps {
  ae: CompanyCourseUser;
  sdr: CompanyCourseUser;
  companyId: string;
  courseId: string;
}

const CommissionControls: FC<{
  companyId: string;
  courseId: string;
  item: CompanyCourseCommission;
}> = ({ companyId, courseId, item }) => {
  const [override, setOverride] = useState(
    item.override != null ? String(item.override) : ""
  );
  const [paymentDate, setPaymentDate] = useState(item.paymentDate ?? "");
  const [updateAsync, { isLoading }] =
    useUpdateCompanyCourseCommissionMutation();

  const effectiveCommission =
    override !== "" ? Number(override) : item.roleDefault;
  const amount = (effectiveCommission / 100) * item.agreementAmount;

  const initialOverride = item.override != null ? String(item.override) : "";
  const initialPaymentDate = item.paymentDate ?? "";
  const isDirty =
    override !== initialOverride || paymentDate !== initialPaymentDate;

  const onSave = () => {
    toast.promise(
      updateAsync([
        companyId,
        courseId,
        item.id,
        {
          commission: override === "" ? null : Number(override),
          paymentDate: paymentDate === "" ? null : paymentDate,
        },
      ]).unwrap(),
      {
        pending: "Enregistrement de la commission...",
        success: "Commission mise à jour 🤩",
        error: {
          render({ data }: ToastContentProps<FetchBaseQueryError>) {
            if (data?.status === 422) {
              return "Les valeurs saisies semblent incorrectes (0 à 100).";
            }
            return "Une erreur est survenue";
          },
        },
      }
    );
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center mb-2">
        <FontAwesomeIcon
          icon={faPercent}
          className="text-primary me-2 fs--1"
        />
        <span className="fw-semi-bold fs--1 text-700">Commission</span>
        {override === "" && (
          <SoftBadge bg="secondary" className="ms-2 fs--2">
            Taux par défaut
          </SoftBadge>
        )}
        <SoftBadge
          bg={paymentDate ? "success" : "danger"}
          className="ms-2 fs--2"
        >
          <FontAwesomeIcon
            icon={paymentDate ? faCircleCheck : faCircleXmark}
            className="me-1"
          />
          {paymentDate ? "Réglée" : "Non réglée"}
        </SoftBadge>
      </div>
      <div className="bg-light rounded-3 p-3">
        <Row className="g-3 align-items-start">
          <Col xs={6} md={3}>
            <Form.Label className="mb-1 fs--2 text-700 text-uppercase">
              Taux
            </Form.Label>
            <InputGroup size="sm">
              <Form.Control
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={override}
                placeholder={String(item.roleDefault)}
                onChange={(e) => setOverride(e.target.value)}
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
            <Form.Text className="text-500 fs--2">
              Défaut : {item.roleDefault}%
            </Form.Text>
          </Col>
          <Col xs={6} md={3}>
            <Form.Label className="mb-1 fs--2 text-700 text-uppercase">
              Montant
            </Form.Label>
            <div className="form-control form-control-sm bg-200 border-0 fw-bold text-900 d-flex align-items-center">
              {(Number.isFinite(amount) ? amount : 0).toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              &nbsp;€
            </div>
            <Form.Text className="text-500 fs--2">
              {effectiveCommission}% de{" "}
              {item.agreementAmount.toLocaleString("fr-FR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              &nbsp;€ accordés
            </Form.Text>
          </Col>
          <Col xs={12} md={4}>
            <Form.Label className="mb-1 fs--2 text-700 text-uppercase">
              Date de règlement
            </Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </Col>
          <Col xs={12} md={2} className="d-grid">
            <Form.Label className="mb-1 fs--2 d-none d-md-block">
              &nbsp;
            </Form.Label>
            <Button
              variant="primary"
              size="sm"
              onClick={onSave}
              disabled={isLoading || !isDirty}
            >
              Enregistrer
            </Button>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const CommissionSkeleton: FC = () => (
  <div className="mt-3 pt-3 border-top">
    <Placeholder as="div" animation="glow" className="mb-2">
      <Placeholder xs={3} />
    </Placeholder>
    <div className="bg-light rounded-3 p-3">
      <Placeholder as="div" animation="glow">
        <Row className="g-3 align-items-start">
          <Col xs={6} md={3}>
            <Placeholder xs={4} className="mb-1" />
            <Placeholder xs={12} size="lg" />
          </Col>
          <Col xs={6} md={3}>
            <Placeholder xs={5} className="mb-1" />
            <Placeholder xs={12} size="lg" />
          </Col>
          <Col xs={12} md={4}>
            <Placeholder xs={8} className="mb-1" />
            <Placeholder xs={12} size="lg" />
          </Col>
          <Col xs={12} md={2} className="d-grid">
            <Placeholder.Button variant="secondary" xs={12} />
          </Col>
        </Row>
      </Placeholder>
    </div>
  </div>
);

const CommissionReadOnly: FC<{ item: CompanyCourseCommission }> = ({ item }) => (
  <div className="mt-3 pt-3 border-top">
    <div className="d-flex align-items-center mb-2">
      <FontAwesomeIcon icon={faPercent} className="text-primary me-2 fs--1" />
      <span className="fw-semi-bold fs--1 text-700">Ma commission</span>
      <SoftBadge
        bg={item.paymentDate ? "success" : "danger"}
        className="ms-2 fs--2"
      >
        <FontAwesomeIcon
          icon={item.paymentDate ? faCircleCheck : faCircleXmark}
          className="me-1"
        />
        {item.paymentDate ? "Réglée" : "Non réglée"}
      </SoftBadge>
    </div>
    <div className="bg-light rounded-3 p-3">
      <Row className="g-3">
        <Col xs={6} md={3}>
          <p className="mb-1 fs--2 text-700 text-uppercase">Taux</p>
          <p className="mb-0 fw-bold text-900">{item.effectiveCommission}%</p>
        </Col>
        <Col xs={6} md={3}>
          <p className="mb-1 fs--2 text-700 text-uppercase">Montant</p>
          <p className="mb-0 fw-bold text-900">
            {(Number.isFinite(item.amount) ? item.amount : 0).toLocaleString(
              "fr-FR",
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}
            &nbsp;€
          </p>
        </Col>
        <Col xs={12} md={4}>
          <p className="mb-1 fs--2 text-700 text-uppercase">
            Date de règlement
          </p>
          <p className="mb-0 fw-semi-bold text-900">
            {item.paymentDate
              ? DateTime.fromISO(item.paymentDate)
                  .setLocale("fr")
                  .toFormat("dd LLL yyyy")
              : "Non réglée"}
          </p>
        </Col>
      </Row>
    </div>
  </div>
);

const ParticipantCommission: FC<{
  isAdmin: boolean;
  currentUserId?: string;
  participantUserId: string;
  courseUserId: string;
  companyId: string;
  courseId: string;
  commissions?: CompanyCourseCommission[];
  isLoading: boolean;
}> = ({
  isAdmin,
  currentUserId,
  participantUserId,
  courseUserId,
  companyId,
  courseId,
  commissions,
  isLoading,
}) => {
  const canSee = isAdmin || currentUserId === participantUserId;
  if (!canSee) return null;

  const item = commissions?.find((c) => c.id === courseUserId);
  if (item) {
    return isAdmin ? (
      <CommissionControls
        companyId={companyId}
        courseId={courseId}
        item={item}
      />
    ) : (
      <CommissionReadOnly item={item} />
    );
  }

  return isLoading ? <CommissionSkeleton /> : null;
};

const CompanyCourseUsersCard: FC<ICompanyCourseUsersCardProps> = ({
  ae,
  sdr,
  companyId,
  courseId,
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const isAdmin = isStrictAdmin(currentUser);
  const isParticipant = [ae, sdr].some(
    (participant) => participant?.companyUser?.userId === currentUser?.id
  );

  const { data: commissions, isLoading: isLoadingCommissions } =
    useGetCompanyCourseCommissionsQuery([companyId, courseId], {
      skip: !isAdmin && !isParticipant,
    });

  return (
    <div>
      {[ae, sdr].map(
        (
          {
            id,
            companyUser: {
              user: { phone, email, fullname },
              userId,
              role,
            },
            createdAt,
          },
          index
        ) => (
          <div
            key={id}
            className={classNames(
              "bg-white dark__bg-1100 p-x1 rounded-3 shadow-sm",
              {
                "mt-3": index !== 0,
              }
            )}
          >
            <Row className="flex-between-center">
              <Col xs={10}>
                <Link to={`/users/${userId}`} className="fw-semi-bold">
                  {fullname}
                </Link>
              </Col>
            </Row>
            <div className="border-bottom border-lg-0 border-0 my-3"></div>
            <Row className="flex-between-center">
              <Col xs={12} lg={8}>
                <EmailLink email={email} className="h6" />
                {phone ? (
                  <PhoneCallLink
                    number={phone}
                    className="d-flex align-items-center gap-1 h6 mb-0 text-600"
                  />
                ) : (
                  <div className="d-flex align-items-center gap-1 h6 mb-0">
                    <FontAwesomeIcon icon={faPhone} transform="shrink-3 up-1" />
                    <UnknownBadge />
                  </div>
                )}
              </Col>

              <Col xs={12} lg={"auto"}>
                <div className="border-bottom border-lg-0 border-0 my-lg-0 my-3"></div>
                <Flex direction="column">
                  <SoftBadge
                    key={role}
                    bg={getColorFromRole([role])}
                    className={classNames({
                      "mt-1": index !== 0,
                    })}
                  >
                    {getReelRoleName([role])}
                  </SoftBadge>
                  <h6 className="mb-0 mt-2">
                    <span className="d-flex align-items-center gap-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        transform="shrink-3 up-1"
                      />
                      <span className="text-600">
                        {DateTime.fromISO(createdAt)
                          .setLocale("fr")
                          .toFormat("dd LLL yyyy")}
                      </span>
                    </span>
                  </h6>
                </Flex>
              </Col>
            </Row>
            <ParticipantCommission
              isAdmin={isAdmin}
              currentUserId={currentUser?.id}
              participantUserId={userId}
              courseUserId={id}
              companyId={companyId}
              courseId={courseId}
              commissions={commissions}
              isLoading={isLoadingCommissions}
            />
          </div>
        )
      )}
    </div>
  );
};

export default CompanyCourseUsersCard;
